import { HubCapsule } from '@aws-amplify/core';
import { CognitoUser } from 'amazon-cognito-identity-js';
import Amplify, {
  Auth,
  Hub
} from 'aws-amplify';
import awsExports from 'aws-exports';
import _ from 'lodash';
import { BehaviorSubject } from 'rxjs';



type AuthChallengeName =
  | 'NEW_PASSWORD_REQUIRED'
  | 'SMS_MFA'
  | 'SOFTWARE_TOKEN_MFA'
  | 'MFA_SETUP';

export type AuthUser = CognitoUser & {
  challengeName: AuthChallengeName // Add missing TypeScript definition
}



export type IStatus =
  'noAttemptMade' |
  'notLoggedIn' |
  'loggedIn';

export interface IAuthState {
  status: IStatus;
  authProviderUserId: string;
  accessToken: string;
}



const __subjAuthState = new BehaviorSubject<IAuthState>({
  status: 'noAttemptMade',
  authProviderUserId: '',
  accessToken: ''
});

let __lastUser: AuthUser | null = null;
let __registerPassword = '';



export async function init() {
  Amplify.configure(awsExports);
  Hub.listen('auth', __handleAuthEvent);
  await __establishCurrentSession();
}



async function __establishCurrentSession() {
  let authState: IAuthState;

  try {
    const session = await Auth.currentSession();
    authState = {
      status: 'loggedIn',
      authProviderUserId: session?.getIdToken().decodePayload()['sub'],
      accessToken: session?.getAccessToken().getJwtToken()
    };

  } catch (err) {
    authState = {
      status: 'notLoggedIn',
      authProviderUserId: '',
      accessToken: ''
    };
  }

  if (process.env.REACT_APP_ENV_NAME !== 'prod') {
    console.log(JSON.stringify({
      'Authorization': `Bearer ${authState.accessToken}`
    }));
  }

  if (!_.isEqual(authState, __subjAuthState.value)) {
    __subjAuthState.next(authState);
  }
  return authState;
}



function __handleAuthEvent(data: HubCapsule) {
  const eventName = data.payload.event;
  if (
    eventName === 'signIn' ||
    eventName === 'signUp' ||
    eventName === 'signOut'
  ) {
    __establishCurrentSession();
  }
}



export function getAuthStateObservable() {
  return __subjAuthState.asObservable();
}



export async function getAuthState(): Promise<IAuthState> {
  return __establishCurrentSession();
}



export interface ISignUpData {
  email: string;
  password: string;
}

export async function register(data: ISignUpData) {
  __registerPassword = data.password;
  const result = await Auth.signUp({
    username: data.email,
    password: data.password,
    attributes: {
      email: data.email
    }
  });

  return result.userSub;
}



export async function confirmRegisterCode(email: string, code: string) {
  await Auth.confirmSignUp(email, code);
}



export async function signInWithRegisteredPassword(email: string): Promise<AuthUser> {
  const signedInUser = await Auth.signIn(email, __registerPassword);
  return signedInUser;
}



export async function resendRegisterCode(email: string) {
  await Auth.resendSignUp(email);
}



export async function logIn({
  email, password
}: {
  email: string;
  password: string;
}) {
  __registerPassword = password;
  __lastUser = await Auth.signIn({
    username: email,
    password
  });

  if (__lastUser && __lastUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
    const pw = prompt('Please reset your password to continue');

    if (pw) {
      await Auth.completeNewPassword(__lastUser, pw);
    }
  }

  __registerPassword = '';
}



export function canConfirmLoginCode() {
  return !!__lastUser;
}



export async function confirmLoginCode(code: string) {
  await Auth.confirmSignIn(__lastUser, code, 'SMS_MFA');
  __lastUser = null;
  await __establishCurrentSession();
}



export async function forgotPassword(email: string) {
  await Auth.forgotPassword(email);
}



export async function forgotPasswordSubmit(email: string, code: string, password: string) {
  await Auth.forgotPasswordSubmit(email, code, password);
}



export async function updatePassword(oldPassword: string, newPassword: string) {
  const user = await Auth.currentAuthenticatedUser();
  await Auth.changePassword(user, oldPassword, newPassword);
}



export async function updatePhoneNumber(phoneNumber: string) {
  const user = await Auth.currentAuthenticatedUser();
  await Auth.updateUserAttributes(user, {
    phone_number: phoneNumber
  });
}



export async function logOut() {
  await Auth.signOut();
  await __establishCurrentSession();
}



export async function updateEmail(email: string) {
  const user = await Auth.currentAuthenticatedUser();
  if (user.attributes.email !== email) {
    await Auth.updateUserAttributes(user, {
      email
    });
  }
}
